import React from "react";
import useTranslation from "next-translate/useTranslation";
import { useFela } from "react-fela";
import Trans from "next-translate/Trans";

import {
  PricingCard,
  IPricingCard,
  Price,
  BulletedList,
  PricingTooltip,
} from "@/components/PricingCard";
import { PricingSubtitle } from "@/containers/PricingSlider/PricingSubtitle";
import { Heading } from "@/components/Heading";
import { useTheme } from "@/hooks/useTheme";
import { ANALYTICS } from "@/constants/analytics";
import { useInquiryForm } from "@/hooks/useInquiryForm";
import { FormSourceType } from "@/containers/InquiryForm";

type IEnterpriseBackupCard = Pick<IPricingCard, "cta"> & {
  price: number;
  amount: number;
};

export const EnterpriseBackupCard: React.FC<IEnterpriseBackupCard> = ({
  cta,
  price,
  amount,
}) => {
  const { t } = useTranslation("section/pricingFigmaBackup");
  const { colors } = useTheme();
  const { css } = useFela();
  const { toggleInquiryForm } = useInquiryForm();

  return (
    <PricingCard
      cta={cta}
      hideSupportedFormats
      customStyle={{
        filter: "drop-shadow(20px 20px 80px rgba(16, 5, 48, 0.08))",
        boxShadow: "none",
        nested: {
          "> div": {
            marginBottom: 24,
          },
        },
      }}
    >
      <Heading
        as="h4"
        customStyle={{ marginBottom: 24, color: colors.CHARCOAL }}
      >
        {t("backup", {
          plan: t("plans.enterprise"),
        })}
      </Heading>
      <Price
        value={price}
        suffix="month"
        customStyle={{
          color: colors.GRAY,
          nested: {
            "> strong": {
              fontSize: 36,
              lineHeight: "46px",
              color: colors.DARK_GREY,
            },
            "> span:first-child": {
              fontWeight: 700,
              verticalAlign: "text-top",
            },
            "> span:last-child span": {
              display: "inline-block",
              marginRight: 2,
            },
          },
        }}
      />
      <PricingSubtitle>{t("includesFiles", { amount })}</PricingSubtitle>

      <BulletedList>
        {t("description.backupCycle", { hours: 6 })}
        {t("description.fileRetention", { days: 180 })}
        <Trans
          i18nKey="section/pricingFigmaBackup:description.backupRegion"
          components={[
            <PricingTooltip
              key="pricing-enterprise-backupRegion"
              text={t("tooltips.s3Regions")}
            />,
          ]}
        />
        {t("description.figFiles")}
        {t("description.ownStorage")}
        {t("description.prioritySupport")}
        <Trans
          i18nKey="section/pricingFigmaBackup:description.sso"
          components={[
            <PricingTooltip
              key="pricing-enterprise-sso"
              text={t("tooltips.sso")}
            />,
          ]}
        />
        <Trans
          i18nKey="section/pricingFigmaBackup:description.sla"
          components={[
            <PricingTooltip
              key="pricing-enterprise-sla"
              text={t("tooltips.sla")}
            />,
          ]}
        />
      </BulletedList>
      <div
        className={css({
          marginTop: 30,
          display: "flex",
          justifyContent: "space-between",
        })}
      />
      <Trans
        i18nKey="section/pricingFigmaBackup:contactUs.title"
        components={[
          <span
            key="contact-us-text"
            className={css({ color: colors.DARK_GREY, fontWeight: 700 })}
          />,
          <button
            type="button"
            aria-label="Contact sales"
            key="contact-us-button"
            id={ANALYTICS.contactUsSubscriptionLinkId}
            onClick={() =>
              toggleInquiryForm(FormSourceType.BACKUP_ENTERPRISE_CARD)
            }
            className={css({
              color: colors.RED,
              fontSize: 16,
              textDecoration: "none",
              backgroundColor: "transparent",
              border: "none",
              fontWeight: 700,
              outline: "none",
              nested: {
                ":hover": {
                  cursor: "pointer",
                },
              },
            })}
          />,
        ]}
      />
    </PricingCard>
  );
};
